/** A live preview of the guest pre-check-in form (plan §3 "Dock — Preview"): a faithful but
 *  simplified replica of the guest app (`bookingmgmt`), drawn client-side from a settings
 *  object, the operator's options and SAMPLE data — never a real guest, and nothing is
 *  sent: typing and the step buttons work inside the frame, and that is all.
 *
 *  - The controls are the console's (step pills, Desktop | Phone, the waiver language);
 *    the guest app's look — Roboto, its blue, 12 px radii, uppercase buttons — lives only
 *    inside the frame, under a nested theme (`guestTheme`).
 *  - `step` is controlled, so a host can follow the section being edited (Party members →
 *    Guest 1). A step the settings do not have (a guest while the party's details are not
 *    collected) shows the lead booker's.
 *  - Desktop draws the guest layout from its `lg` breakpoint (1024 px) scaled to the
 *    frame; Phone draws it at 390 px (scaled down when the frame is narrower). The guest
 *    app decides its layout by media query; here the width is the preview's choice.
 *  - Settings changes render through `useDeferredValue`, so typing in the editor never
 *    waits for the frame. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Box, MenuItem, ScopedCssBaseline, TextField, ThemeProvider, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useCallback, useDeferredValue, useId, useMemo, useState } from 'react';

import { Segmented } from '@/components/Segmented';
import { useElementWidth } from '@/features/records/page/recordPage';

import { languageName } from './guest/GuestChrome';
import type { FieldValues } from './guest/GuestFields';
import { GuestPage } from './guest/GuestPage';
import { guestTheme } from './guestTheme';
import { DESKTOP_CANVAS_PX, PHONE_CANVAS_PX } from './guestTokens';
import {
  previewSteps,
  resolveStep,
  SAMPLE_GUESTS,
  shownWaivers,
  stepFields,
  waiverLanguages,
  withDefaults,
  type GuestPreviewOptions,
  type GuestPreviewSample,
  type GuestPreviewSettings,
  type GuestPreviewStep,
} from './previewModel';

export type GuestPreviewDevice = 'desktop' | 'phone';

export interface GuestFormPreviewProps {
  /** A pre-check settings object (the draft's, or the stored one). */
  settings: GuestPreviewSettings;
  /** The operator's tag categories (tags, derived required / multiple choice) and enabled
   *  waivers (languages, text per language). */
  options: GuestPreviewOptions;
  step: GuestPreviewStep;
  onStepChange: (step: GuestPreviewStep) => void;
  /** Sample guests and template values (default `SAMPLE_GUESTS`). */
  sample?: GuestPreviewSample;
  /** The width the preview opens at (default Phone — about the dock's own width). */
  defaultDevice?: GuestPreviewDevice;
}

const DEVICES = [
  { value: 'desktop' as const, label: 'Desktop' },
  { value: 'phone' as const, label: 'Phone' },
];

export function GuestFormPreview({ settings, options, step, onStepChange, sample = SAMPLE_GUESTS, defaultDevice = 'phone' }: GuestFormPreviewProps) {
  const deferred = useDeferredValue(settings);
  const steps = useMemo(() => previewSteps(deferred, sample), [deferred, sample]);
  const current = resolveStep(step, steps);
  const [device, setDevice] = useState<GuestPreviewDevice>(defaultDevice);
  const wide = device === 'desktop';

  // What a guest typed, per participant, seeded from the sample; and the fields they left.
  const [values, setValues] = useState<FieldValues[]>(() => sample.participants.map((v) => ({ ...v })));
  const [touched, setTouched] = useState<Record<string, Record<string, boolean>>>({});
  const participant = current.participant;
  const onChange = useCallback(
    (id: string, value: unknown) => {
      if (participant === null) return;
      setValues((prev) => {
        const next = [...prev];
        next[participant] = { ...(prev[participant] ?? {}), [id]: value };
        return next;
      });
    },
    [participant],
  );
  const onBlur = useCallback(
    (id: string) => setTouched((prev) => (prev[current.id]?.[id] ? prev : { ...prev, [current.id]: { ...prev[current.id], [id]: true } })),
    [current.id],
  );

  const waivers = useMemo(() => shownWaivers(deferred, options), [deferred, options]);
  const languages = useMemo(() => waiverLanguages(waivers), [waivers]);
  const [lang, setLang] = useState<string | null>(null);
  const pickedLang = lang && languages.includes(lang) ? lang : (languages[0] ?? null);

  const [frameRef, frameWidth] = useElementWidth<HTMLDivElement>();
  const canvas = wide ? DESKTOP_CANVAS_PX : PHONE_CANVAS_PX;
  const scale = frameWidth > 0 ? Math.min(1, frameWidth / canvas) : 1;
  const off = withDefaults(deferred).enabled !== true;
  // The frame's "Sample guests · nothing is sent" is its accessible description: a screen
  // reader entering the region hears that it is sample data, not a real guest's form.
  const sampleId = useId();

  return (
    <Box data-guest-preview sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
      <Box sx={{ overflowX: 'auto', minWidth: 0 }}>
        <Segmented
          label="Preview step"
          value={current.id}
          onChange={onStepChange}
          options={steps.map((s) => ({ value: s.id, label: s.label }))}
          fill={false}
          dataAttr="guest-preview-steps"
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 40 }}>
        <Segmented label="Preview width" value={device} onChange={setDevice} options={DEVICES} fill={false} />
        {languages.length > 1 && (
          <TextField
            select
            size="small"
            label="Waiver language"
            value={pickedLang ?? ''}
            onChange={(e) => setLang(e.target.value)}
            sx={{ width: 168 }}
          >
            {languages.map((l) => (
              <MenuItem key={l} value={l}>
                {languageName(l)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      {off && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-guest-preview-off>
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px' }} />
          Pre-check-in is off: guests see “Pre-check-in Not Available” instead of this form.
        </Typography>
      )}
      <Box
        ref={frameRef}
        role="region"
        aria-label="Guest form preview"
        aria-describedby={`${sampleId}-what ${sampleId}-note`}
        data-guest-frame
        data-device={device}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.default', minWidth: 0 }}
      >
        <Box
          data-guest-sample
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, minHeight: 28, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <ScienceOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography id={`${sampleId}-what`} variant="caption" sx={{ fontWeight: 600 }}>
            Sample
          </Typography>
          <Typography id={`${sampleId}-note`} variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            guests · nothing is sent
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flex: 'none', fontVariantNumeric: 'tabular-nums' }}>
            {canvas} px{scale < 1 ? ` · ${Math.round(scale * 100)}%` : ''}
          </Typography>
        </Box>
        <Box sx={{ width: canvas, zoom: scale, mx: 'auto' }}>
          <ThemeProvider theme={guestTheme}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ScopedCssBaseline>
                <GuestPage
                  settings={deferred}
                  options={options}
                  sample={sample}
                  steps={steps}
                  current={current}
                  onStep={onStepChange}
                  fields={participant === null ? [] : stepFields(deferred, participant)}
                  values={values}
                  touched={touched[current.id] ?? {}}
                  onChange={onChange}
                  onBlur={onBlur}
                  waivers={waivers}
                  lang={pickedLang}
                  onLang={setLang}
                  wide={wide}
                />
              </ScopedCssBaseline>
            </LocalizationProvider>
          </ThemeProvider>
        </Box>
      </Box>
    </Box>
  );
}
